import { create } from 'zustand';

/**
 * Selected-team store — owns what the right-side drawer (or mobile bottom
 * sheet) is currently showing. Two presentations:
 *
 *   - `kind: 'team'`  → single-team drawer (TEAM panel comes from PAGE-001)
 *   - `kind: 'city'`  → city drawer placeholder for cluster click
 *   - `kind: null`    → drawer closed
 *
 * The store stays narrow: just identifiers. Hooks/queries hanging off the
 * drawer fetch the actual data so we don't need to invalidate this store on
 * every API refetch.
 */
export type DrawerSelection =
  | { kind: 'team'; teamNumber: string }
  | { kind: 'city'; citySlug: string; city: string; country: string | null }
  | { kind: null };

export interface SelectedTeamStore {
  selection: DrawerSelection;
  isOpen: boolean;
  openTeam: (teamNumber: string) => void;
  openCity: (city: { citySlug: string; city: string; country: string | null }) => void;
  close: () => void;
}

const closedState = { selection: { kind: null } as DrawerSelection, isOpen: false };

export const useSelectedTeamStore = create<SelectedTeamStore>((set) => ({
  ...closedState,
  openTeam: (teamNumber) =>
    set({ selection: { kind: 'team', teamNumber }, isOpen: true }),
  openCity: ({ citySlug, city, country }) =>
    set({
      selection: { kind: 'city', citySlug, city, country },
      isOpen: true,
    }),
  close: () => set(closedState),
}));
